import {
  useState,
  useEffect,
  useMemo,
} from 'react';
import {
  Outlet,
  useLocation,
  useMatches,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  Default,
  Switch,
  Case,
  When,
} from 'react-if';
import { ToastContainer } from 'react-toastify';
import { MixpanelEvents, OrganisationFeatureFlags } from 'shared';
import { IntercomProps, useIntercom } from 'react-use-intercom';
import { useDispatch, useSelector } from 'react-redux';
import { setUserId } from 'firebase/analytics';
import * as Sentry from '@sentry/react';

import { LogRocket } from '../../common/lib/logrocket';
import { useGetSelectedOrganisation } from '../hooks/useGetSelectedOrganisation';
import { env } from '../../common/lib/env';
import { useUser } from '../hooks/useUser';
import { useOrganisations } from '../hooks/useOrganisations';
import { useActionTableModels } from '../hooks/useActions';
import { useContactParams } from '../hooks/useContacts';
import { useOnboarding } from '../hooks/useOnboarding';
import { useSidebarLayout } from '../hooks/useSidebarLayout';
import { useMobileNumberCheckToast } from '../hooks/useMobileNumberCheck';
import { classNames } from '../../common/lib/classNames';
import { mixpanelService } from '../../common/lib/mixpanel';
import { RootState } from '../store';
import { analytics } from '../lib/analytics';
import { setState } from '../../common/slices/genericState';
import { getLastSelectedOrgId, setLastSelectedOrgId } from '../lib/lastSelectedOrg';
import { Card } from '../../common/Atoms/Card';
import { MultiButtonCardHeader } from '../../common/Atoms/MultiButtonCardHeader';

import type { RouteHandle } from './Routes';
import { DesktopSidebar } from './Navigation/DesktopSidebar';
import { NestedSidebar } from './Navigation/NestedSidebar';
import { Sidebar } from './Navigation/Sidebar';
import { Navigation } from './Navigation/Navigation';
import { NotificationArea } from './NotificationArea';
import { Spinner } from './Common/Spinner';
import { SubscriptionStrap } from './SubscriptionStrap';
import { AdminOverrideStrap } from './AdminOverrideStrap';
import { RevertLateFeeModal } from './Modals/RevertLateFee';
import { ActionDrawer } from './Actions/ActionDrawer';
import { MaintenanceBanner } from './MaintenanceBanner';
import { BookCallModal } from './Modals/BookCall';
import { AdhocActionModal } from './Modals/AdhocActionModal/AdhocActionModal';
import { MobileNumberCheckModal } from './Modals/MobileNumberCheckModal';
import { MobileNumberFixModal } from './Modals/MobileNumberFixModal';
import { StatementPreview } from './Modals/StatementPreview';
import { WelcomeModal } from './Modals/WelcomeModal';
import { OneOffLateFeeModal } from './Modals/CreateNewModals/OneOffLateFee';
import { HelpLauncher } from './HelpLauncher';
import { AssistantDrawer } from './Assistant/AssistantDrawer';
import { AssistantLauncher } from './Assistant/AssistantLauncher';

export function Root() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const {
    data: organisations,
    isLoading: organisationLoading,
    isError: organisationError,
    isFetching: organisationFetching,
    fulfilledTimeStamp: organisationFulfilledAt,
    refetch: refetchOrganisations,
  } = useOrganisations();
  const {
    data: user,
    isLoading: userLoading,
    isError: userError,
    isFetching: userFetching,
    refetch: refetchUser,
  } = useUser();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const currentOrg = useGetSelectedOrganisation();
  const { resetParams: resetContactParams } = useContactParams();
  const { resetModels } = useActionTableModels();
  const { isOnboarding } = useOnboarding();
  const { contentMarginClass, nestedType } = useSidebarLayout();
  const matches = useMatches();

  // Wide data-table pages opt out of the content max-width (via their route
  // handle) so the table uses the whole screen instead of scrolling
  // horizontally on wide monitors
  const isFullWidth = matches.some(match => !!(match.handle as RouteHandle | undefined)?.fullWidth);
  const { boot } = useIntercom();
  const genericState = useSelector((state: RootState) => state.genericState);
  const dispatch = useDispatch();

  const hasBooted = genericState.hasBootedTracking;

  // A failed first load leaves no orgs, which would otherwise show the
  // "connect to Xero or QuickBooks" screen as if the user had none.
  // Failed polls keep the last good data, so this only hits on a cold start.
  // Orgs check the fulfilled timestamp since a good response can be empty.
  const loadFailed = (userError && !user) || (organisationError && !organisationFulfilledAt);

  function retryLoad() {
    if (userError) refetchUser();
    if (organisationError) refetchOrganisations();
  }

  // Show toast when actions are generated
  // DISABLED TEMP AS LOCKS PER CONTACT
  // useActionCreateStatus();

  // Progress toast for the "check all mobile numbers" task
  useMobileNumberCheckToast();

  const {
    returnToXeroTenantId,
    loginSource,
    redirectToOrgId,
    redirectToId,
    redirectToType,
  } = useMemo(() => {
    return {
      returnToXeroTenantId: searchParams.get(`returnToXeroTenantId`),
      loginSource: searchParams.get(`loginSource`),
      redirectToType: searchParams.get(`redirectToType`),
      redirectToId: searchParams.get(`redirectToId`),
      redirectToOrgId: searchParams.get(`redirectToOrgId`),
    };
  }, [searchParams]);

  useEffect(() => {
    if (currentOrg) {
      // Already have an org, do nothing
      return;
    }

    if (!organisations || organisations.length === 0 || pathname !== `/`) {
      // No orgs to select from or not root url
      return;
    }

    let url = `/${organisations[0].id}`;

    // Default to the most recently viewed org (this tab) when just landing
    // on the root url. Connect/redirect params below take precedence.
    const lastOrgId = getLastSelectedOrgId();
    if (lastOrgId && organisations.some(org => org.id === lastOrgId)) {
      url = `/${lastOrgId}`;
    }

    if (returnToXeroTenantId) {
      const org = organisations.find(org => org.externalId === returnToXeroTenantId) || organisations[0];
      url = `/${org.id}`;
    }

    if (redirectToOrgId) {
      const org = organisations.find(org => org.id === redirectToOrgId) || organisations[0];
      url = `/${org.id}`;

      if (redirectToType === `invoice` && redirectToId) {
        url = `${url}/invoices/${redirectToId}`;
      }

      if (redirectToType === `invoice_notes` && redirectToId) {
        url = `${url}/invoices/${redirectToId}/notes`;
      }
    }

    navigate(url);

  }, [pathname, organisations, returnToXeroTenantId, redirectToId, redirectToType, redirectToOrgId, currentOrg]);

  useEffect(() => {
    if (currentOrg) {
      // Reset the states
      resetContactParams();
      resetModels();
      // Todo invoices

      setLastSelectedOrgId(currentOrg.id);
    }
  }, [currentOrg?.id]);

  useEffect(() => {
    if (loginSource) {
      if (loginSource === `app_store_button`) {
        mixpanelService.trackMixpanelEvent(MixpanelEvents.LoginFromXeroAppStore);
      }
    }
  }, [loginSource]);

  useEffect(() => {
    if (user?.id) {
      Sentry.setUser({ id: user.id, email: user.email, username: user.name });
    }
  }, [user?.id]);

  useEffect(() => {
    if (!hasBooted && user?.id) {
      const bootData: IntercomProps = {
        email: user.email,
        name: user.name,
        userId: user.id,
        userHash: user.intercomHash,
        customLauncherSelector: `#intercom-chat-start`,
        // The floating Intercom bubble gets in the way - we show our own
        // small edge tab instead (HelpLauncher)
        hideDefaultLauncher: true,
      };

      // only boot intercom in prod, and when not impersonating a user
      if (!user?.adminOverride) {
        boot(bootData);
      }

      if (analytics) {
        setUserId(analytics, user.id);
      }

      mixpanelService.identify(user.id);

      if (env.isProd && user.id) {
        LogRocket.identify(user.id, {
          email: user.email,
          name: user.name,
        });
      }

      dispatch(setState({ hasBootedTracking: true }));
    }
  }, [user?.id, hasBooted]);

  // Page body shared by both frames: toasts, loading states, and the routed
  // content with its width constraint
  const pageBody = (
    <>
      <ToastContainer
        position={ `bottom-center` }
        autoClose={ 2000 }
        hideProgressBar={ false }
        newestOnTop={ true }
        closeOnClick
        draggable
        pauseOnHover
        theme={ `colored` }
        className={ `toast-container` }
      />
      <Switch>
        <Case condition={ userLoading }>
          <div>
            <Spinner message={ `Loading your account details...` }
              center />
          </div>
        </Case>
        <Case condition={ organisationLoading }>
          <div>
            <Spinner message={ `Loading your connected organisation details...` }
              center />
          </div>
        </Case>
        <Case condition={ loadFailed }>
          <div className={ `py-6 mx-auto px-4 sm:px-6 md:px-8 max-w-8xl` }>
            <Card>
              <MultiButtonCardHeader
                title={ `We can't reach Paidnice right now` }
                description={ `Something went wrong loading your account. This is usually temporary, please try again in a moment. If it keeps happening, contact hello@paidnice.com.` }
                buttons={ [{
                  buttonText: `Retry`,
                  onClick: retryLoad,
                  loading: userFetching || organisationFetching,
                }] }
              />
            </Card>
          </div>
        </Case>
        <Default>
          <div className={ `py-6` }>
            <div className={
              classNames(
                `mx-auto px-4 sm:px-6 md:px-8`,
                isFullWidth ? `max-w-none` : `max-w-8xl`,
              ) }>
              <When condition={ !pathname.startsWith(`/billing`) }>
                <NotificationArea />
              </When>
              { /* Where the route content is loaded */ }
              <Outlet />
            </div>
          </div>
        </Default>
      </Switch>
    </>
  );

  const modals = (
    <>
      { /* Modals & Drawers  */ }
      <RevertLateFeeModal />
      <ActionDrawer />
      <BookCallModal />
      <AdhocActionModal />
      <MobileNumberCheckModal />
      <MobileNumberFixModal />
      <StatementPreview />
      <WelcomeModal />
      <OneOffLateFeeModal />
      { /* Sticky help tab replacing the default Intercom bubble */ }
      <HelpLauncher />
      <When condition={ !!currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_ASSISTANT) || !!user?.adminOverride }>
        <AssistantLauncher />
        <AssistantDrawer />
      </When>
    </>
  );

  return (
    <>
      { modals }
      <div>
        <When condition={ !isOnboarding }>
          <Sidebar
            currentOrg={ currentOrg }
            setSidebarOpen={ setSidebarOpen }
            sidebarOpen={ sidebarOpen }
          />
          <DesktopSidebar currentOrg={ currentOrg } />
        </When>
        <div className={
          classNames(
            `flex flex-1 flex-col`,
            // Only leave space for side nav when not onboarding
            !isOnboarding && contentMarginClass,
          ) }>
          <MaintenanceBanner />
          <SubscriptionStrap />
          <AdminOverrideStrap />
          <Navigation
            setSidebarOpen={ setSidebarOpen }
          />
          <div className={ `flex flex-1` }>
            { /* Nested submenu sidebar (saved filters etc) - sits below the top nav */ }
            <When condition={ !isOnboarding && !!nestedType }>
              { () => (
                <div className={ `sticky top-16 hidden h-[calc(100vh-4rem)] shrink-0 md:block` }>
                  <NestedSidebar type={ nestedType } />
                </div>
              ) }
            </When>
            { /* Main Content Area */ }
            <main
              id={ `main-content` }
              className={ `min-w-0 flex-1` }
            >
              { pageBody }
            </main>
          </div>
        </div>
      </div>
    </>
  );
}
